// frontend/src/components/Common/Comments/CommentsPanel.jsx
//
// Props:
//   entityType / entityId        – parent entity (comments always stored here)
//   subEntityType / subEntityId  – optional child (e.g. a task) — new comments are
//                                  tagged with it, and the panel can filter by it
//   subEntityLabel               – reference text snapshot saved with new comments
//   nested                       – opened from inside another modal (raises z-index)

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { commentsAPI } from '../../../api/client';
import { useTranslation } from '../../../context/TranslationContext';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../hooks/useToast';
import Toast from '../Toast';
import { FiMessageSquare, FiCheckCircle, FiTag } from 'react-icons/fi';
import CommentItem from './CommentItem';
import CommentForm from './CommentForm';
import './Comments.css';

const SCOPE = { SUB: 'SUB', ALL: 'ALL' };

const CommentsPanel = ({
  entityType,
  entityId,
  subEntityType,
  subEntityId,
  subEntityLabel,
  isOpen,
  onClose,
  nested = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toasts, removeToast, toast_error } = useToast();
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const hasSub = !!(subEntityType && subEntityId);
  const [scope, setScope] = useState(hasSub ? SCOPE.SUB : SCOPE.ALL);

  // Reset scope whenever the panel is (re)opened for a different target
  useEffect(() => {
    if (isOpen) setScope(hasSub ? SCOPE.SUB : SCOPE.ALL);
  }, [isOpen, hasSub, subEntityId]);

  // Filter sent to the API — undefined means "all comments of the entity"
  const filter = hasSub && scope === SCOPE.SUB
    ? { subEntityType, subEntityId }
    : undefined;

  const loadComments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await commentsAPI.getComments(entityType, entityId, filter);
      if (response.data.success) {
        setComments(response.data.data);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, scope, subEntityType, subEntityId]);

  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await commentsAPI.getUnreadCount(entityType, entityId, filter);
      if (response.data.success) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, scope, subEntityType, subEntityId]);

  useEffect(() => {
    if (isOpen && entityId) {
      loadComments();
      loadUnreadCount();
    }
  }, [isOpen, entityId, loadComments, loadUnreadCount]);

  const handleCommentAdded = (newComment) => {
    setComments(prev => [newComment, ...prev]);  // newest first
    loadUnreadCount();
  };

  const handleCommentError = (error) => {
    toast_error(error);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await commentsAPI.markAllAsViewed(entityType, entityId, filter);
      setUnreadCount(0);
      setComments(prev => prev.map(comment => ({ ...comment, is_viewed: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleCommentViewed = (commentId) => {
    setComments(prev => prev.map(comment =>
      comment.comment_id === commentId
        ? { ...comment, is_viewed: true }
        : comment
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // While open inside a modal, Escape closes only the panel (not the host modal)
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopImmediatePropagation();
        onClose?.();
      }
    };
    // capture phase so it runs before the host modal's own Escape listener
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Portal to <body> so the panel is never clipped or stacked under a modal.
  // React events still bubble through the React tree, so every click is
  // stopped here to keep it from reaching the host modal's overlay onClose.
  return createPortal(
    <div
      className={`comments-panel-overlay${nested ? ' comments-panel-overlay--nested' : ''}`}
      onClick={(e) => { e.stopPropagation(); onClose?.(); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="comments-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="comments-header">
          <div className="comments-title">
            <FiMessageSquare size={20} />
            <h3>{t('comments.title', 'Comments')}</h3>
            {unreadCount > 0 && (
              <span className="comments-unread-badge">{unreadCount}</span>
            )}
          </div>
          <div className="comments-header-actions">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="btn-mark-read"
                title={t('comments.mark_all_read', 'Mark all as read')}
              >
                <FiCheckCircle size={16} />
                {t('comments.mark_all_read', 'Mark all as read')}
              </button>
            )}
            <button onClick={onClose} className="btn-close-panel">
              ✕
            </button>
          </div>
        </div>

        {/* Sub-entity context + scope toggle */}
        {hasSub && (
          <div className="comments-scope-bar">
            {subEntityLabel && (
              <div className="comments-scope-label" title={subEntityLabel}>
                <FiTag size={12} /> {subEntityLabel}
              </div>
            )}
            <div className="comments-scope-toggle">
              <button
                type="button"
                className={scope === SCOPE.SUB ? 'active' : ''}
                onClick={() => setScope(SCOPE.SUB)}
              >
                {t('comments.scope_task', 'Esta tarefa')}
              </button>
              <button
                type="button"
                className={scope === SCOPE.ALL ? 'active' : ''}
                onClick={() => setScope(SCOPE.ALL)}
              >
                {t('comments.scope_all', 'Todo o pedido')}
              </button>
            </div>
          </div>
        )}

        {/* Comments List */}
        <div className="comments-list">
          {loading ? (
            <div className="comments-loading">
              {t('loading', 'Loading...')}
            </div>
          ) : comments.length === 0 ? (
            <div className="comments-empty">
              <FiMessageSquare size={48} />
              <p>{t('comments.no_comments', 'No comments yet')}</p>
              <p className="comments-empty-subtitle">
                {t('comments.be_first', 'Be the first to add a comment')}
              </p>
            </div>
          ) : (
            comments.map((comment) => (
              <CommentItem
                key={comment.comment_id}
                comment={comment}
                onViewed={handleCommentViewed}
                currentUserId={user?.user_id}
                // In "this task" view the tag is redundant
                hideSubEntity={hasSub && scope === SCOPE.SUB}
              />
            ))
          )}
        </div>

        {/* Comment Form — new comments are always tagged with the sub-entity */}
        <div className="comments-footer">
          <CommentForm
            entityType={entityType}
            entityId={entityId}
            subEntityType={hasSub ? subEntityType : null}
            subEntityId={hasSub ? subEntityId : null}
            subEntityLabel={hasSub ? subEntityLabel : null}
            onCommentAdded={handleCommentAdded}
            onError={handleCommentError}
          />
        </div>

        <div className="comments-toast-container">
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>

      </div>
    </div>,
    document.body
  );
};

export default CommentsPanel;